import React, {
  useContext,
  useEffect,
  useState,
  SyntheticEvent,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

//Material UI
import Toolbar from "@mui/material/Toolbar";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Card from "@mui/material/Card";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tab,
  Tabs,
} from "@mui/material";

//Custom components
import FormContent from "@/components/FormContent";
import { GridContainer } from "@/ui/Grid";
import {
  CardActionButtonDelete,
  CardActionButtonSave,
  CardHeading,
  CardToolbarButtonBack,
} from "@/ui/Card";
import { FormAvatar, FormHeading } from "@/ui/Form";
import useFormController from "@/components/useFormController";
import { FormContentMode } from "@/@types/components/formContent";
import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import dataController from "@/lib/dataController";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import FormFillContent from "@/ui/Form/FormFillContent";
import FormHeadingID from "@/ui/Form/FormHeadingId";
import { Cancel, CheckCircle, HomeWork } from "@mui/icons-material";
import DialogToolbarMenuButton from "@/ui/Dialog/DialogToolbarMenuButton";
import DialogToolbarMenuItem from "@/ui/Dialog/DialogToolbarMenuItem";
import modelUser from "@/models/user";
import { DCRecord } from "@/@types/lib/dataController";

interface IProjectFormProps {
  dc: dataController;
  recordId: number;
  mode: FormContentMode;
  form: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function ProjectForm(props: IProjectFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;

  const { dc, recordId, mode, form } = props;

  const [allOperatorRecords, setAllOperatorRecords] = useState<DCRecord[]>([]);
  const [tabValue, setTabValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const allOperatorsApiPath = modelUser.apiPath + "operators";
  const dcUserOperators = new dataController(modelUser, allOperatorsApiPath);

  const getAllOperatorRecords = () => {
    dcUserOperators.GetData().then((resp) => {
      if (resp.data && Array.isArray(resp.data)) {
        setAllOperatorRecords(resp.data);
      }
    });
  };

  useEffect(() => {
    getAllOperatorRecords();
  }, []);

  const {
    record,
    validation,
    fields,
    onFieldChange,
    doUpdate,
    doDelete,
    doClose,
    doActivate,
    dataChanged,
  } = useFormController({
    mode,
    recordId: recordId,
    form,
    dc,
    onClose: () => {},
  });

  const operatorsOnProject = useMemo(() => {
    return (record.operators as number[]) ?? [];
  }, [record]);

  const handleClickBack = () => {
    if (doClose) {
      doClose()
        .then((resp) => {
          if (resp.success) {
            navigate("/projects");
          } else if (resp.shouldSave) {
            if (doUpdate) {
              doUpdate()
                .then((result) => {
                  if (result.success) {
                    const msg = "messages.record_update_success";
                    snackbarContext.showNotification(msg, "success");
                    navigate("/projects");
                  }
                })
                .catch((result) => {
                  if (
                    result.success === false &&
                    result.validationPass === false
                  ) {
                    //do nothing
                  } else {
                    const msg = "messages.record_update_error";
                    snackbarContext.showNotification(msg, "error");
                  }
                });
            }
          }
        })
        .catch((result) => {
          snackbarContext.showNotification(result.error, "error");
        });
    }
  };

  const handleUpdate = (evt: any) => {
    if (doUpdate) {
      //update user
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = "messages.record_update_success";
            snackbarContext.showNotification(msg, "success");
            if (recordId !== record.id) {
              navigate("/projects");
            }
          }
        })
        .catch((result) => {
          if (result.success === false && result.validationPass === false) {
            //do nothing
          } else {
            const msg = "messages.record_update_error";
            snackbarContext.showNotification(msg, "error");
          }
        });
    }
  };

  const handleDelete = (evt: any) => {
    if (doDelete) {
      doDelete()
        .then((result) => {
          if (result.success) {
            const msg = "messages.record_delete_success";
            snackbarContext.showNotification(msg, "success");
            navigate("/projects");
          }
        })
        .catch((result) => {
          const msg = "messages.record_delete_error";
          snackbarContext.showNotification(msg, "error");
        });
    }
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        doActivate(true).then((result) => {
          if (result) {
            const msg = t("messages.record_activate_success");
            snackbarContext.showNotification(msg, "success");
            navigate("/projects");
          } else {
            const msg = t("messages.record_activate_error");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("messages.record_activate_error");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        doActivate(false).then((result) => {
          if (result) {
            const msg = t("messages.record_deactivate_success");
            snackbarContext.showNotification(msg, "success");
            navigate("/projects");
          } else {
            const msg = t("messages.record_deactivate_error");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("erromessagesr.record_deactivate_error");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const handleFieldChange = (value: any, source: string) => {
    // tag ends up in folder names, keep it in the shape the DB CHECK accepts
    const val = source === "tag" && typeof value === "string" ? value.toUpperCase().replace(/\s/g, "_") : value;
    onFieldChange(val, source);
  };

  const commonProps = {
    record: record,
    validation: validation,
    onFieldChange: handleFieldChange,
    fields: fields,
    mode: mode,
    fieldNames: dc.getFormFieldsNames(form),
    //pickerDataControllers: pickerDataControllers,
  };

  const handleOperatorCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedId = parseInt(event.target.value);
    let updatedOperators = [...operatorsOnProject];

    if (event.target.checked) {
      if (!updatedOperators.includes(selectedId)) {
        updatedOperators.push(selectedId);
      }
    } else {
      updatedOperators = updatedOperators.filter((id) => id !== selectedId);
    }

    onFieldChange(updatedOperators, "operators");
  };

  return (
    <Card>
      <CardHeading>
        <Toolbar variant="dense" disableGutters={true}>
          <FormAvatar ariaLabel="project" icon={HomeWork} />
          <FormHeading
            header={`${t("titles.project")}: ${record.naziv}`}
            subheader={<FormHeadingID id={recordId} />}
            dataChanged={dataChanged}
            lowercase={false}
            children={undefined}
          />
          <FormFillContent />
          {mode === "update" ? (
            <DialogToolbarMenuButton>
              {record.active ? (
                <DialogToolbarMenuItem
                  onClick={handleDeactivate}
                  label={t("buttons.deactivate")}
                  icon={<Cancel color="action" />}
                />
              ) : (
                <DialogToolbarMenuItem
                  onClick={handleActivate}
                  label={t("buttons.activate")}
                  icon={<CheckCircle color="action" />}
                />
              )}
            </DialogToolbarMenuButton>
          ) : null}
          <CardToolbarButtonBack onClick={handleClickBack} />
        </Toolbar>
      </CardHeading>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleChange}
              aria-label="basic tabs example"
              variant="fullWidth"
            >
              <Tab label={t("titles.basic_info")} {...a11yProps(0)} />
              <Tab label={t("titles.operators")} {...a11yProps(1)} />
            </Tabs>
          </Box>
        </Box>
        <CustomTabPanel value={tabValue} index={0}>
          <GridContainer>
            <FormContent {...commonProps} columns={2} />
          </GridContainer>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <Box>
            <FormGroup
              sx={{ height: "60vh", display: "flex", flexDirection: "column" }}
            >
              {allOperatorRecords.map((op) => {
                return (
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={op.id}
                        checked={operatorsOnProject.includes(op.id as number)}
                        onChange={handleOperatorCheckedChange}
                      />
                    }
                    label={op.first_name + " " + op.last_name}
                  />
                );
              })}
            </FormGroup>
          </Box>
        </CustomTabPanel>
      </CardContent>
      <CardActions>
        <FormFillContent />
        <CardActionButtonDelete onClick={handleDelete} variant="outlined" />
        <CardActionButtonSave
          onClick={handleUpdate}
          variant="contained"
          disabled={!dataChanged}
        />
      </CardActions>
    </Card>
  );
}

export default ProjectForm;
